import type React from 'react';
import classes from './availability-page.module.scss';
import {
  getCurrentWeekStartIsoDate,
  formatWeekRange,
  shiftIsoDate,
  formatWeekdayLabel,
  formatDateDot,
} from '../../utils/date.utils';
import { useAvailabilityPageController } from './useAvailabilityPageController';
import { AvailabilityGovernanceControls } from './AvailabilityGovernanceControls';
import { EmployeeLimitWarnings } from '../../components/EmployeeLimitWarnings';

export const AvailabilityPage: React.FC = () => {
  const model = useAvailabilityPageController();
  const {
    weekStartDate,
    setWeekStartDate,
    week,
    isLoadingWeek,
    weekError,
    noteContent,
    setNoteContent,
    isSavingNote,
    isWeekBusy,
    dayUiState,
    loadWeek,
    periodRows,
    onTogglePeriod,
    mutateWeeklyNotes,
    canEditDate,
    canEditNotes,
  } = model;

  if (isLoadingWeek && !week) {
    return (
      <section className={`contentCard ${classes.availabilityCard}`} dir="rtl">
        <h1 className="homeTitle">זמינות</h1>
        <p className="homeHint">טוען זמינות שבועית...</p>
      </section>
    );
  }

  if (weekError && !week) {
    return (
      <section className={`contentCard ${classes.availabilityCard}`} dir="rtl">
        <h1 className="homeTitle">זמינות</h1>
        <p className="errorText inlineMessage">{weekError}</p>
        <button
          className="button retryButton"
          type="button"
          onClick={() => {
            void loadWeek(weekStartDate);
          }}
        >
          נסה שוב
        </button>
      </section>
    );
  }

  return (
    <div className="workspaceLayout" dir="rtl">
      <aside className="contentCard workspaceSidebar">
        <div className="workspaceSidebarHeader">
          <h1 className="homeTitle">זמינות</h1>
          <p className="homeHint">עדכון זמינות אישית</p>
        </div>

        <p className="workspaceWeekLabel">
          {week ? formatWeekRange(week.weekStartDate, week.weekEndDate) : ''}
        </p>
        <EmployeeLimitWarnings status={week?.weeklyLimits} counts={model.weeklyLimitCounts}
          outdated={model.weeklyLimitsOutdated} refreshDisabled={isWeekBusy} onRefresh={() => void model.refreshWeeklyLimits()} />

        <div className="workspaceWeekNav">
          <button
            type="button"
            className="navArrowButton"
            onClick={() => setWeekStartDate((current) => shiftIsoDate(current, -7))}
            disabled={isWeekBusy}
          >
            שבוע קודם
          </button>
          <button
            type="button"
            className="navArrowButton"
            onClick={() => setWeekStartDate(getCurrentWeekStartIsoDate())}
            disabled={isWeekBusy}
          >
            השבוע הנוכחי
          </button>
          <button
            type="button"
            className="navArrowButton"
            onClick={() => setWeekStartDate((current) => shiftIsoDate(current, 7))}
            disabled={isWeekBusy}
          >
            שבוע הבא
          </button>
        </div>

        <AvailabilityGovernanceControls {...model} />

        <section
          className={`${classes.weeklyNotesEditor} ${classes.availabilitySidebarNotes}`}
          aria-labelledby="weekly-notes-heading"
        >
          <div className={classes.weeklyNotesHeader}>
            <h3 id="weekly-notes-heading">הערות שלך</h3>
            <span>{week ? formatWeekRange(week.weekStartDate, week.weekEndDate) : ''}</span>
          </div>
          <textarea
            className={classes.weeklyNotesTextarea}
            aria-label="ההערות שלך..."
            placeholder="ההערות שלך..."
            value={noteContent}
            onChange={(event) => setNoteContent(event.target.value)}
            onContextMenu={(event) => event.preventDefault()}
            disabled={!canEditNotes || isLoadingWeek || isSavingNote}
          />
          <div className={classes.weeklyNotesActions}>
            <button
              type="button"
              className={classes.weeklyNotesDefaultButton}
              onClick={() => {
                void mutateWeeklyNotes('saveDefault');
              }}
              disabled={!canEditNotes || isWeekBusy}
            >
              שמור כברירת מחדל ☆
            </button>
            <button
              type="button"
              className={classes.weeklyNotesDeleteButton}
              onClick={() => {
                void mutateWeeklyNotes('deleteDefault');
              }}
              disabled={!canEditNotes || isWeekBusy}
            >
              מחק ברירת מחדל ☆
            </button>
            <button
              type="button"
              className={classes.weeklyNotesSaveButton}
              onClick={() => {
                void mutateWeeklyNotes('saveWeek');
              }}
              disabled={!canEditNotes || isWeekBusy}
            >
              {isSavingNote ? 'שומר...' : 'שמור'}
            </button>
          </div>
        </section>

        <p className={classes.workspaceSidebarHint}>בחרו שבוע ועדכנו זמינות לכל משמרת.</p>
      </aside>

      <section className={`contentCard workspaceMain ${classes.availabilityCard}`}>
        <div className={classes.workspaceContentHeader}>
          <div>
            <h2 className="homeTitle">זמינות שבועית</h2>
            <p className="homeHint">{week ? formatWeekRange(week.weekStartDate, week.weekEndDate) : ''}</p>
          </div>
        </div>

        {isLoadingWeek && <p className="homeHint">טוען שבוע...</p>}
        {weekError && <p className="errorText inlineMessage">{weekError}</p>}

        <div className={classes.availabilityMatrixWrap}>
          <table role="table" className={`mobileRecordTable ${classes.availabilityMatrix}`}>
            <thead>
              <tr>
                <th scope="col" className={classes.availabilityRowHeading}>
                  זמינות
                </th>
                {week?.days.map((day) => (
                  <th key={day.dateDay} scope="col">
                    <span className={classes.availabilityMatrixDay}>{formatWeekdayLabel(day.dateDay)}</span>
                    <span className={classes.availabilityMatrixDate}>
                      {formatDateDot(day.dateDay).slice(0, 5)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {periodRows.length === 0 && (
                <tr>
                  <td className={classes.availabilityEmptyCell} colSpan={(week?.days.length ?? 0) + 1}>
                    לא הוגדרו משמרות למחלקה שלך.
                  </td>
                </tr>
              )}
              {periodRows.map((periodRow) => (
                <tr key={periodRow.sectionShiftsPk ?? 'full-day'}>
                  <th scope="row" className={classes.availabilityRowHeading}>
                    <span className={classes.availabilityShiftName}>{periodRow.shiftName}</span>
                    {periodRow.sectionName && (
                      <span className={classes.availabilitySectionName}>{periodRow.sectionName}</span>
                    )}
                  </th>
                  {week?.days.map((day) => {
                    const period = day.periods.find(
                      (candidate) => candidate.sectionShiftsPk === periodRow.sectionShiftsPk,
                    );
                    if (!period) {
                      return (
                        <td key={day.dateDay} data-label={`${formatWeekdayLabel(day.dateDay)} · ${formatDateDot(day.dateDay)}`} className={classes.availabilityEmptyCell}>
                          —
                        </td>
                      );
                    }
                    const stateKey = `${day.dateDay}:${period.sectionShiftsPk}`;
                    const state = dayUiState[stateKey];
                    const isSaving = state?.isSaving === true;
                    const isAvailable = period.isAvailable;

                    return (
                      <td key={day.dateDay} data-label={`${formatWeekdayLabel(day.dateDay)} · ${formatDateDot(day.dateDay)}`}>
                        <span className={classes.availabilityPeriodTime}>
                          {period.startHour && period.endHour
                            ? `${period.startHour} - ${period.endHour}`
                            : 'שעות טרם הוגדרו'}
                        </span>
                        <button
                          type="button"
                          className={`${classes.availabilityMatrixToggle} ${isAvailable ? classes.available : classes.unavailable}`}
                          onClick={() => {
                            void onTogglePeriod(day, period);
                          }}
                          disabled={!canEditDate(day.dateDay) || isSaving || isSavingNote || isLoadingWeek}
                          aria-pressed={isAvailable}
                          aria-label={`${formatWeekdayLabel(day.dateDay)}, ${period.shiftName}: ${isAvailable ? 'זמין' : 'לא זמין'}`}
                        >
                          <span className={classes.availabilityMatrixSymbol} aria-hidden="true">
                            {isSaving ? '…' : isAvailable ? '✓' : '×'}
                          </span>
                          <span>{isSaving ? 'שומר' : isAvailable ? 'זמין' : 'לא זמין'}</span>
                        </button>
                        {state?.error && <p className={classes.availabilityCellError}>{state.error}</p>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
